import { inject, Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, combineLatest, merge, Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { ApiService } from '../../api-service/api.service';
import { AppConfigProvider, AppConfigService } from '../../app-config.service';
import { AccessService, Feature, StubAccessService } from '../access';
import { MARKET_ID_PARAM } from './constants';
import { Market } from './market';
import { toSignal } from '@angular/core/rxjs-interop';
import { PermissionsApiService } from '@vendasta/platform-users';

@Injectable({ providedIn: 'root' })
export class MarketsService {
  public readonly markets$ = this.appConfig.config$.pipe(
    switchMap(() => this.api.get('/_ajax/v1/marketplace/market/list')),
    map((markets) => markets.map((market) => new Market(market)) as Market[]),
    map((markets) =>
      markets.sort((a, b) => {
        const aName = a.name.toLowerCase(),
          bName = b.name.toLowerCase();

        return aName < bName ? -1 : 1;
      }),
    ),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  private defaultMarket$ = this.markets$.pipe(
    map((markets) => {
      let defaultMarket = markets.find((market) => market.market_id === 'default');
      if (!defaultMarket) {
        defaultMarket = markets[0];
      }
      return defaultMarket;
    }),
  );

  platformPermissionsService = inject(PermissionsApiService);

  public crmMarket$ = this.appConfig.config$.pipe(
    switchMap((config) => {
      const partnerId = config.partnerId;
      const userId = config.unifiedUserId;
      return this.platformPermissionsService.getPermissions({ partnerId, userId });
    }),
    map((resp) => {
      return resp.permissions?.restrictedToMarketId;
    }),
    catchError(() => of('')),
    shareReplay(1),
  );

  private userSelectedMarketId$$ = new ReplaySubject<string>(1);
  private appSelectedMarketId$$: Observable<string> = this.defaultMarket$.pipe(
    map((defaultMarket) => {
      const params = this.route.snapshot.queryParams;
      // tslint:disable-next-line:no-string-literal
      let marketIdQueryParam = params['marketID'] || params[MARKET_ID_PARAM];
      if (marketIdQueryParam) {
        if (marketIdQueryParam === 'null') {
          marketIdQueryParam = null;
        }
        return marketIdQueryParam;
      } else {
        return defaultMarket?.market_id;
      }
    }),
  );

  selectedMarketId$ = merge(this.appSelectedMarketId$$, this.userSelectedMarketId$$).pipe(shareReplay(1));

  private selectedMarket$ = combineLatest([this.markets$, this.selectedMarketId$]).pipe(
    map(([markets, selectedMarketId]) => markets.find((market) => market.market_id === selectedMarketId)),
    filter((market) => !!market),
  );

  public readonly marketsEnabled$ = this.accessService.hasAccessToFeature(Feature.markets).pipe(distinctUntilChanged());

  public readonly adminCanAccessAllPartnerMarkets$ = this.appConfig.config$.pipe(
    map((config) => config?.accessibleMarketIds?.length === 0),
  );

  public readonly userAccessibleMarkets$ = combineLatest([
    this.appConfig.config$.pipe(map((config) => config.accessibleMarketIds)),
    this.markets$,
    this.crmMarket$,
  ]).pipe(
    map(([accessibleMarketIds, markets, crmMarket]) => {
      let filteredMarkets = markets;
      if (accessibleMarketIds?.length || 0 > 0) {
        filteredMarkets = markets.filter((m: Market) => accessibleMarketIds.indexOf(m.market_id) >= 0);
      }
      if (crmMarket && crmMarket !== '') {
        filteredMarkets = markets.filter((m: Market) => m.market_id === crmMarket);
      }
      return filteredMarkets;
    }),
  );
  userAccessibleMarkets = toSignal(this.userAccessibleMarkets$);

  // if the partner does not have the default market, we will use the first marketId in their list
  public defaultMarketId = 'default';

  constructor(
    private api: ApiService,
    private route: ActivatedRoute,
    @Inject(StubAccessService) private accessService: AccessService,
    @Inject(AppConfigService) private appConfig: AppConfigProvider,
  ) {}

  get markets(): Observable<Market[]> {
    return this.markets$;
  }

  get currentMarket$(): Observable<Market> {
    return this.selectedMarket$;
  }

  selectMarket(market: Market): void {
    if (!market) {
      return;
    }
    this.selectMarketByID(market.market_id);
  }

  selectMarketByID(marketId: string): void {
    if (
      !this.userAccessibleMarkets() ||
      !this.userAccessibleMarkets().find((market) => market.market_id === marketId)
    ) {
      return;
    }
    this.userSelectedMarketId$$.next(marketId);
  }

  selectDefaultMarket(): void {
    this.selectMarketByID(this.defaultMarketId);
  }

  getMarkets(): Observable<Market[]> {
    return this.markets$;
  }

  getMarket(marketId: string): Observable<Market> {
    return this.markets$.pipe(
      map((markets) => {
        return markets.find((market) => (market.market_id ? market.market_id : '') === marketId);
      }),
    );
  }
}
